import { ModuleClient } from "@formant/core/client/ModuleClient";
import { ModuleConfigurationClient } from "@formant/core/client/ModuleConfigurationClient";
import { defined } from "@formant/core/common/defined";
import { IModuleStore } from "@formant/core/model/data/IModuleStore";
import { DefinedEntity } from "@formant/core/model/DefinedEntity";
import { IModule } from "@formant/core/model/IModule";
import { IModuleConfiguration } from "@formant/core/model/IModuleConfiguration";
import { Uuid } from "@formant/core/model/Uuid";

import { customModuleList } from "../utils/builtInCustomModuleList";
import { IAuthStore } from "./IAuthStore";
import { StoreCache } from "./StoreCache";

export class ModuleStore implements IModuleStore {
    private listCache = new StoreCache<undefined, DefinedEntity<IModule>[]>();
    private cache = new StoreCache<Uuid, DefinedEntity<IModule> | false>();

    constructor(
        private moduleClient: ModuleClient,
        private moduleConfigurationClient: ModuleConfigurationClient,
        private authStore: IAuthStore,
    ) {}

    private getBuildInCustomModules(): DefinedEntity<IModule>[] {
        return [...customModuleList.filter(_ => _.id !== undefined)];
    }

    public listAll(): DefinedEntity<IModule>[] | undefined {
        return [
            ...(this.listCache.get(
                undefined,
                async () => await this.fetchList(),
            ) || []),
            ...this.getBuildInCustomModules(),
        ];
    }

    public async listAllAsync(): Promise<DefinedEntity<IModule>[] | undefined> {
        const data = await this.fetchList();
        return [...(data || []), ...this.getBuildInCustomModules()];
    }

    public get(id: string): DefinedEntity<IModule> | false | undefined {
        if (id.startsWith("formant:")) {
            const found = this.getBuildInCustomModules().find(_ => _.id === id);
            return found || false;
        }
        return this.cache.get(
            id,
            async () => (await this.fetchGet(id)) || false,
        );
    }

    public fetch(id: string): Promise<DefinedEntity<IModule> | false> {
        if (id.startsWith("formant:")) {
            const found = this.getBuildInCustomModules().find(_ => _.id === id);
            return Promise.resolve(found || false);
        }
        return this.fetchGet(id);
    }

    public async create(
        customModule: IModule,
    ): Promise<DefinedEntity<IModule>> {
        const created = await this.moduleClient.create(
            await this.authStore.token(),
            customModule,
        );
        this.cache.set(defined(created.id), created);
        this.listCache.clear();
        return created;
    }

    public async update(
        id: Uuid,
        customModule: Partial<DefinedEntity<IModule>>,
    ): Promise<DefinedEntity<IModule>> {
        const updated = await this.moduleClient.update(
            await this.authStore.token(),
            id,
            customModule,
        );
        this.cache.set(id, updated);
        this.listCache.clear();
        return updated;
    }

    public save(customModule: IModule): Promise<IModule> {
        const { id } = customModule;
        if (id) {
            return this.update(id, customModule);
        } else {
            return this.create(customModule);
        }
    }

    public async delete(id: Uuid) {
        this.cache.set(id, false);
        await this.moduleClient.delete(await this.authStore.token(), id);
        await this.fetchList();
    }

    private async fetchGet(id: Uuid): Promise<DefinedEntity<IModule> | false> {
        const customModule = await this.moduleClient.get(
            await this.authStore.token(),
            id,
        );
        const value = customModule || false;
        this.cache.set(id, value);
        return value;
    }

    private async fetchList(): Promise<DefinedEntity<IModule>[]> {
        const value = await this.moduleClient.list(
            await this.authStore.token(),
        );
        value.forEach(_ => this.cache.set(defined(_.id), _));
        this.listCache.set(undefined, value);
        return value;
    }

    public async createConfiguration(configuration: IModuleConfiguration) {
        return await this.moduleConfigurationClient.create(
            await this.authStore.token(),
            configuration,
        );
    }

    public async getConfiguration(id: Uuid) {
        return await this.moduleConfigurationClient.get(
            await this.authStore.token(),
            id,
        );
    }

    public async updateConfiguration(
        id: Uuid,
        configuration: Partial<IModuleConfiguration>,
    ) {
        return await this.moduleConfigurationClient.update(
            await this.authStore.token(),
            id,
            configuration,
        );
    }

    public async upsertConfiguration(configuration: IModuleConfiguration) {
        return await this.moduleConfigurationClient.upsert(
            await this.authStore.token(),
            configuration,
        );
    }

    public async deleteConfiguration(id: Uuid) {
        return await this.moduleConfigurationClient.delete(
            await this.authStore.token(),
            id,
        );
    }
}
